import { useState } from "react";

import { Box, Grid, SimpleGrid } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useTranslation } from "react-i18next";

import { MonthPicker, TextInput } from "components";
import { CreditRequestForm } from "types";
import { AppUtils } from "utils";

import CITY_DATA from "../../../../../../assets/data/cityData.json";
import POSTAL_DATA from "../../../../../../assets/data/postalData.json";

import SectionTitle from "../../../components/section-title";

interface CreditRequestPersonalAddressProps {
  form: UseFormReturnType<CreditRequestForm>;
  visibleFields: string[];
  readOnly?: boolean;
  isBankCredit?: boolean;
}

const CreditRequestPersonalAddress = ({
  form,
  visibleFields,
  readOnly = false,
  isBankCredit = false,
}: CreditRequestPersonalAddressProps) => {
  const { t } = useTranslation();
  const [editingField, setEditingField] = useState<string | null>(null);

  const getCityFromPostal = (postalValue: string): string | undefined => {
    if (AppUtils.isEmpty(postalValue) || postalValue.length < 4) {
      return undefined;
    }
    return (POSTAL_DATA as Record<string, string>)[postalValue];
  };

  const getPostalFromCity = (cityValue: string): string | undefined => {
    if (AppUtils.isEmpty(cityValue)) {
      return undefined;
    }
    const cityData = CITY_DATA as Record<string, string>;
    if (cityData[cityValue]) {
      return cityData[cityValue];
    }
    const lowerCityValue = cityValue.toLowerCase();
    const matchingKey = Object.keys(cityData).find((key) => key.toLowerCase() === lowerCityValue);
    return matchingKey ? cityData[matchingKey] : undefined;
  };

  const getFieldKey = (fieldName: string) => {
    // Use stable key for the field being edited, form.key for others
    return editingField === fieldName ? fieldName : form.key(fieldName);
  };

  const handlePostalChange = (
    value: string,
    postalField: "postal" | "previousPostal",
    cityField: "city" | "previousCity",
  ) => {
    form.setFieldValue(postalField, value);

    const city = getCityFromPostal(value);
    if (city) {
      form.setFieldValue(cityField, city, { forceUpdate: true });
    }
  };

  const handleCityChange = (
    value: string,
    cityField: "city" | "previousCity",
    postalField: "postal" | "previousPostal",
  ) => {
    form.setFieldValue(cityField, value);

    const postal = getPostalFromCity(value);
    if (postal) {
      form.setFieldValue(postalField, postal, { forceUpdate: true });
    }
  };

  return (
    <Box>
      <SectionTitle label={t("user.address.label")} isBankCredit={isBankCredit} />
      <SimpleGrid cols={4}>
        <Grid>
          <Grid.Col span={5}>
            <TextInput
              label={t("user.address.postal.label")}
              key={getFieldKey("postal")}
              {...form.getInputProps("postal")}
              maxLength={4}
              required={true}
              onFocus={() => setEditingField("postal")}
              onChange={(event) => handlePostalChange(event.currentTarget.value, "postal", "city")}
              disabled={readOnly}
            />
          </Grid.Col>
          <Grid.Col span={7}>
            <TextInput
              label={t("user.address.city.label")}
              key={getFieldKey("city")}
              {...form.getInputProps("city")}
              required={true}
              onFocus={() => setEditingField("city")}
              onChange={(event) => handleCityChange(event.currentTarget.value, "city", "postal")}
              disabled={readOnly}
            />
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span={7}>
            <TextInput
              label={t("user.address.street.label")}
              key={form.key("street")}
              {...form.getInputProps("street")}
              required={true}
              disabled={readOnly}
            />
          </Grid.Col>
          <Grid.Col span={5}>
            <TextInput
              label={t("user.address.houseNumber.label")}
              key={form.key("houseNumber")}
              {...form.getInputProps("houseNumber")}
              onBeforeInput={(event: any) => {
                const cursorPos = event.currentTarget.selectionStart || 0;
                if (cursorPos === 0) {
                  const inputValue = (event as any).data;
                  if (inputValue && !/^[0-9]/.test(inputValue)) {
                    event.preventDefault();
                  }
                }
              }}
              required={true}
              disabled={readOnly}
            />
          </Grid.Col>
        </Grid>
        <MonthPicker
          label={t("user.livingFrom.label")}
          key={form.key("livingFrom")}
          {...form.getInputProps("livingFrom")}
          maxDate={new Date()}
          required={true}
          disabled={readOnly}
        />
      </SimpleGrid>
      {(visibleFields.includes("previousPostal") ||
        visibleFields.includes("previousCity") ||
        visibleFields.includes("previousStreet") ||
        visibleFields.includes("previousHouseNumber")) && (
        <Box>
          <SectionTitle label={t("user.address.previousLabel")} isBankCredit={isBankCredit} />
          <SimpleGrid cols={4} mt={"sm"}>
            <Grid>
              {visibleFields.includes("previousPostal") && (
                <Grid.Col span={5}>
                  <TextInput
                    label={t("user.address.postal.label")}
                    key={getFieldKey("previousPostal")}
                    {...form.getInputProps("previousPostal")}
                    required={true}
                    onFocus={() => setEditingField("previousPostal")}
                    onChange={(event) =>
                      handlePostalChange(
                        event.currentTarget.value,
                        "previousPostal",
                        "previousCity",
                      )
                    }
                    disabled={readOnly}
                  />
                </Grid.Col>
              )}
              {visibleFields.includes("previousCity") && (
                <Grid.Col span={7}>
                  <TextInput
                    label={t("user.address.city.label")}
                    key={getFieldKey("previousCity")}
                    {...form.getInputProps("previousCity")}
                    required={true}
                    onFocus={() => setEditingField("previousCity")}
                    onChange={(event) =>
                      handleCityChange(event.currentTarget.value, "previousCity", "previousPostal")
                    }
                    disabled={readOnly}
                  />
                </Grid.Col>
              )}
            </Grid>
            <Grid>
              {visibleFields.includes("previousStreet") && (
                <Grid.Col span={7}>
                  <TextInput
                    label={t("user.address.street.label")}
                    key={form.key("previousStreet")}
                    {...form.getInputProps("previousStreet")}
                    required={true}
                    disabled={readOnly}
                  />
                </Grid.Col>
              )}
              {visibleFields.includes("previousHouseNumber") && (
                <Grid.Col span={5}>
                  <TextInput
                    label={t("user.address.houseNumber.label")}
                    key={form.key("previousHouseNumber")}
                    {...form.getInputProps("previousHouseNumber")}
                    onBeforeInput={(event: any) => {
                      const cursorPos = event.currentTarget.selectionStart || 0;
                      if (cursorPos === 0) {
                        const inputValue = (event as any).data;
                        if (inputValue && !/^[0-9]/.test(inputValue)) {
                          event.preventDefault();
                        }
                      }
                    }}
                    required={true}
                    disabled={readOnly}
                  />
                </Grid.Col>
              )}
            </Grid>
          </SimpleGrid>
        </Box>
      )}
    </Box>
  );
};

export default CreditRequestPersonalAddress;
